/**
 * preQuoteSlice — feature-owned Redux slice for the Pre-Quote working copy (spec 057 T020).
 * Mirrors the module `verificationSlice` shape (constitution §67/68): `current` / `server` +
 * `BaseSliceState` edit tracking, hydrated from RTK Query reads/writes via `extraReducers`.
 */
import { createSelector, createSlice, current as draftCurrent } from '@reduxjs/toolkit';
import type { CaseReducer, PayloadAction } from '@reduxjs/toolkit';
import type { BaseSliceState } from '../../../../../app/sliceUtils';
import {
  addEditedField,
  createEditTrackingReducers,
  createInitialState,
  createValidationReducers,
} from '../../../../../app/sliceUtils';
import type { RootState } from '../../../../../app/store';
import type { ProcedureReimbursementResult } from '../../../data';
import { preQuoteEndpoints } from './preQuoteEndpoints';
import type {
  PreQuoteBenefitsView,
  PreQuotePatientResponsibilityView,
  PreQuoteView,
} from './preQuoteViews';

export interface PreQuoteSliceState extends BaseSliceState<PreQuoteView> {
  /** Last-known server state (a `structuredClone` of the loaded/saved pre-quote) — backs discard. */
  server: PreQuoteView | null;
}

export const initialState: PreQuoteSliceState = {
  ...createInitialState<PreQuoteView>(),
  server: null,
};

/** Shared reset applied whenever the working copy is rebaselined to a persisted pre-quote — both
 * loading a fresh record and a successful save land in the identical state, but remain two
 * separately-named reducers below since they represent distinct lifecycle events. */
const rebaselinePreQuote = (state: PreQuoteSliceState, payload: PreQuoteView): void => {
  state.current = payload;
  state.server = structuredClone(payload);
  state.isEdited = false;
  state.editedFields = [];
  state.validationErrors = {};
};

/** Load a pre-quote into the working copy and clear edit tracking (read / create success). */
const setPreQuote: CaseReducer<PreQuoteSliceState, PayloadAction<PreQuoteView>> = (state, action) => {
  rebaselinePreQuote(state, action.payload);
};

const clearPreQuote: CaseReducer<PreQuoteSliceState> = (state) => {
  state.current = null;
  state.server = null;
  state.isEdited = false;
  state.editedFields = [];
  state.validationErrors = {};
};

/** Shallow-merges a partial and marks the touched fields edited (mirrors `editVerification`). */
const editPreQuote: CaseReducer<PreQuoteSliceState, PayloadAction<Partial<PreQuoteView>>> = (
  state,
  action,
) => {
  if (!state.current) return;
  Object.assign(state.current, action.payload);
  for (const field of Object.keys(action.payload)) addEditedField(state, field);
};

/** Save success: re-baseline `server` to the persisted pre-quote and clear dirty tracking. */
const savePreQuoteSuccess: CaseReducer<PreQuoteSliceState, PayloadAction<PreQuoteView>> = (
  state,
  action,
) => {
  rebaselinePreQuote(state, action.payload);
};

/** Restores `current` from the `server` baseline and clears edit tracking (discard). */
const discardPreQuoteEdits: CaseReducer<PreQuoteSliceState> = (state) => {
  if (state.server) state.current = structuredClone(draftCurrent(state.server));
  state.isEdited = false;
  state.editedFields = [];
  state.validationErrors = {};
};

const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

const emptyBenefits: PreQuoteBenefitsView = {
  deductible: 0,
  deductibleMet: 0,
  outOfPocket: 0,
  outOfPocketMet: 0,
  coInsurance: 0,
  coPay: 0,
  dedApplyToOOP: false,
  coPayApplyToOOP: false,
  paidToPhysician: 0,
  uncoveredAmount: 0,
  isComboCase: null,
  hasCoInsuranceMax: false,
  coInsuranceMax: 0,
};

const emptyPatientResponsibility: PreQuotePatientResponsibilityView = {
  deductibleDue: 0,
  coInsuranceDue: 0,
  copayDue: 0,
  collectFromPatient: 0,
  changeNote: '',
  changeNoteCategory: '',
};

/**
 * Populates EDI-derived benefit values from a successful `checkPreQuoteEligibility` (spec 057 T031/
 * T032). Partial-merge preserves any already-set field (e.g. a user-entered uncovered amount) and
 * marks 'benefits' edited so the existing PUT persists.
 */
const populateBenefits: CaseReducer<PreQuoteSliceState, PayloadAction<Partial<PreQuoteBenefitsView>>> = (
  state,
  action,
) => {
  if (!state.current) return;
  state.current.benefits = { ...(state.current.benefits ?? emptyBenefits), ...action.payload };
  addEditedField(state, 'benefits');
};

/**
 * Applies calculator reimbursement results to the working procedures (spec 057 T031/T032, mirrors
 * the verification-slice `applyReimbursementResults`). Matches each result to a row by **code +
 * priority**; on the case-rate fallback the whole total lands on the priority-1 row and the rest are
 * zeroed. Marks 'procedures' edited so the existing PUT persists; never persists itself.
 */
const applyReimbursementResults: CaseReducer<
  PreQuoteSliceState,
  PayloadAction<{ results: ProcedureReimbursementResult[]; totalReimbursement: number; caseRate: boolean }>
> = (state, action) => {
  if (!state.current) return;
  const { results, totalReimbursement, caseRate } = action.payload;
  const procs = state.current.procedures;

  if (caseRate) {
    procs.forEach((p) => {
      p.procedureReimbursement = p.priority === 1 ? round2(totalReimbursement) : 0;
      p.implantReimbursement = 0;
      p.reimbursement = round2(p.procedureReimbursement + p.implantReimbursement);
    });
  } else {
    procs.forEach((p) => {
      const match = results.find((r) => r.code === p.code && r.priority === p.priority);
      p.procedureReimbursement = match ? round2(match.procedureReimbursement) : 0;
      p.implantReimbursement = match ? round2(match.implantReimbursement) : 0;
      p.reimbursement = round2(p.procedureReimbursement + p.implantReimbursement);
    });
  }
  addEditedField(state, 'procedures');
};

/**
 * Applies an EPR calculation breakdown from `calculatePreQuotePatientResponsibility` (spec 057 T031/
 * T032). Partial-merge marks 'patientResponsibility' edited so the existing PUT persists.
 */
const applyPatientResponsibility: CaseReducer<
  PreQuoteSliceState,
  PayloadAction<Partial<PreQuotePatientResponsibilityView>>
> = (state, action) => {
  if (!state.current) return;
  state.current.patientResponsibility = {
    ...(state.current.patientResponsibility ?? emptyPatientResponsibility),
    ...action.payload,
  };
  addEditedField(state, 'patientResponsibility');
};

/**
 * Reflects a successful `checkPreQuoteSecondaryCoverage` back onto the working copy (spec 057 T077).
 * The route ALREADY persisted `status`/`checkedAt` server-side (`RecordSecondaryCoverage`), so this
 * rebaselines BOTH `current` and `server` — never marks the pre-quote edited/dirty.
 */
const applySecondaryCoverageCheck: CaseReducer<
  PreQuoteSliceState,
  PayloadAction<{ status: 'Active' | 'Inactive' | 'Unverified'; checkedAt: string }>
> = (state, action) => {
  if (!state.current?.secondaryCoverage) return;
  state.current.secondaryCoverage.status = action.payload.status;
  state.current.secondaryCoverage.checkedAt = action.payload.checkedAt;
  if (state.server?.secondaryCoverage) {
    state.server.secondaryCoverage.status = action.payload.status;
    state.server.secondaryCoverage.checkedAt = action.payload.checkedAt;
  }
};

const editTrackingReducers = createEditTrackingReducers<PreQuoteSliceState>();
const validationReducers = createValidationReducers<PreQuoteSliceState>();

const preQuoteSlice = createSlice({
  name: 'preQuote',
  initialState,
  reducers: {
    setPreQuote,
    clearPreQuote,
    editPreQuote,
    savePreQuoteSuccess,
    populateBenefits,
    applyReimbursementResults,
    applyPatientResponsibility,
    applySecondaryCoverageCheck,
    ...editTrackingReducers,
    ...validationReducers,
    // Server-restoring discard (overrides the no-op edit-tracking `discardChanges`).
    discardChanges: discardPreQuoteEdits,
  },
  extraReducers: (builder) => {
    builder.addMatcher(preQuoteEndpoints.endpoints.getPreQuoteById.matchFulfilled, (state, action) => {
      setPreQuote(state, action);
    });
    builder.addMatcher(preQuoteEndpoints.endpoints.createPreQuote.matchFulfilled, (state, action) => {
      setPreQuote(state, action);
    });
    builder.addMatcher(preQuoteEndpoints.endpoints.updatePreQuote.matchFulfilled, (state, action) => {
      savePreQuoteSuccess(state, action);
    });
  },
});

export const preQuoteActions = preQuoteSlice.actions;

export const {
  populateBenefits: populateBenefitsAction,
  applyReimbursementResults: applyReimbursementResultsAction,
  applyPatientResponsibility: applyPatientResponsibilityAction,
  applySecondaryCoverageCheck: applySecondaryCoverageCheckAction,
  setPreQuote: setPreQuoteAction,
  clearPreQuote: clearPreQuoteAction,
  editPreQuote: editPreQuoteAction,
  savePreQuoteSuccess: savePreQuoteSuccessAction,
  markFieldAsEdited: markPreQuoteFieldAsEditedAction,
  clearEditedFields: clearPreQuoteEditedFieldsAction,
  markAsSaved: markPreQuoteAsSavedAction,
  discardChanges: discardPreQuoteChangesAction,
  setValidationError: setPreQuoteValidationErrorAction,
  clearValidationError: clearPreQuoteValidationErrorAction,
  clearAllValidationErrors: clearAllPreQuoteValidationErrorsAction,
} = preQuoteSlice.actions;

export default preQuoteSlice.reducer;

// ── Selectors ────────────────────────────────────────────────────────
const selectSlice = (state: RootState): PreQuoteSliceState => state.preQuote;

export const selectCurrentPreQuote = createSelector(selectSlice, (s) => s.current);
export const selectServerPreQuote = createSelector(selectSlice, (s) => s.server);
export const selectIsPreQuoteEdited = createSelector(selectSlice, (s) => s.isEdited);
export const selectPreQuoteEditedFields = createSelector(selectSlice, (s) => s.editedFields);
export const selectPreQuoteValidationErrors = createSelector(selectSlice, (s) => s.validationErrors);
